// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The Edit ⇄ Test flip, and the bench (M13-10, 06 §9).
//
// Build mode draws the machine at a scale sized to the author's reach — the *bench* — and Test
// play grows it to 1:1 on the floor in front of them, because 11 §4's reason for headset test
// play is to "feel their machine at 1:1 before publishing". Back to build puts it back on the
// bench **exactly** where the author left it. This file owns those three placements and the
// decision between them, and nothing else: it does not run the game, build the runtime, or move
// anything. It answers; `XRRuntime` applies.
//
// **Every placement here is stated as distance, bearing and eye drop from the head**, which is
// the frame this repository requires a placement claim to be made in and the frame it turned out
// a placement has to be *stored* in: a world coordinate is not a place, because the runtime
// renumbers its tracking space on every recentre. `placementInFrontOf` already derives the
// distance from the cabinet's own depth (`standOffFor`), the bearing from the head, and the yaw
// from `yawFacing`; the bench adds one number, the drop.
//
// **The full placement is the product's own Full placement.** Same stand-off, same facing, on the
// floor — the one `recenter` and Scale & Place produce — so Test at full lands the lockdown bar at
// the cabinet's own bar height and the near edge at `PLAYER_GAP`, and M13-11 can measure exactly
// that on a device.
//
// **Refused rather than clamped**, for the ritual's reason: a full-size cabinet has to stand on
// something, and there is no honest height between the floor and the bench. When the room-fit
// check says the play space is short, or the eyeline rule says a full-size bar would be above
// the author's eyes, the test runs *in place* at bench scale with a notice. The physics is
// identical at every scale (`placement.ts`: positions scale and velocities do not), so it is a
// real test either way.
//
// **The bench comes back to the centimetre because it was never recomputed.** `begin` keeps a
// copy of the placement the author left and `end` returns that copy; nothing between them reads
// the head again. The author will have stepped back to play a full-size machine, and a bench
// re-derived from where they now stand would be a bench that moved.

import { testPlayVerdict, type TestPlayVerdict } from '../../builder/playable'
import { barIsPlayable } from '../anchors'
import { SEATED_BAR_BELOW_EYELINE, SEATED_HEIGHT } from '../comfort'
import type { Placement } from '../placement'
import type { RoomFit } from '../placementRitual'
import { levelForward, placementInFrontOf, type LevelDirection } from '../viewerRay'
import { BENCH_EN, benchCount, type BenchStrings } from './benchStrings'

/**
 * The bench scale.
 *
 * **0.40, from 0.33, on the second device day of M13**: after an hour of picking and moving parts
 * at 0.33 the author asked for "a 20 % increase in machine size" and the bench "closer to the
 * user" — a pop bumper's 32 mm skirt was 11 mm on the bench, under the 14 mm this repository
 * already calls the width a fingertip misses. At 0.40 it is 13 mm and every handle catch grows
 * with it. The far edge stays where it was: 0.33 was chosen so that, with the near edge at
 * `PLAYER_GAP` (0.45 m), the far edge of a 1.295 m cabinet landed at 0.45 + 0.33 × 1.295 ≈ 0.88 m —
 * `ARM_REACH` (0.85) to within a hand, so a seated author reaches every part without leaning —
 * and `BENCH_GAP` below moves the near edge in by the same amount the scale moves the far edge
 * out: 0.36 + 0.40 × 1.295 ≈ 0.88 m. Inside 06 §3's tabletop range (0.30–0.60), so nothing about
 * a bench-scale machine is a scale the product has not already agreed to draw.
 */
export const BENCH_SCALE = 0.4

/**
 * The gap between the author and the bench's near edge, metres.
 *
 * 0.36: `PLAYER_GAP` is 0.45 for a machine to *play* — a plunger to reach for, flippers to stand
 * at — and a bench is worked over, not stood at. See `BENCH_SCALE` for the arithmetic that keeps
 * the far edge at arm's reach; a 0.20 m near edge, say, would put the tray in the author's lap.
 */
export const BENCH_GAP = 0.36

/**
 * How far below a **standing** author's eyeline the bench's lockdown bar sits, metres.
 *
 * 0.42. A workbench, not a cabinet: a standing player's hands fall onto a bar 0.74 m below their
 * eyes because they *rest* there, and an author works *over* the bench with their hands moving
 * across it, which wants it nearer elbow height. Seated authors get the product's own measured
 * `SEATED_BAR_BELOW_EYELINE` (0.30), which was already the shallow band a seated player can reach.
 * **Judgement, not evidence** — the seated number was tuned on a device and this one was not;
 * M13-11 measures it and this is the constant it will move. `partsTray.test.ts` holds its reach
 * claim against the same 0.42.
 */
export const BENCH_BAR_BELOW_EYE = 0.42

/** A head, as the runtime reports it: position, yaw, and the forward vector where there is one. */
export interface HeadPose {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly yaw: number
  readonly forward?: { readonly x: number; readonly y: number; readonly z: number } | undefined
}

/** What the bench needs to know about the cabinet. */
export interface BenchCabinet {
  readonly length: number
  readonly lockdownBarHeight: number
}

function facing(head: HeadPose, toward?: LevelDirection): LevelDirection {
  if (toward !== undefined) return toward
  const f = head.forward
  return f === undefined ? levelForward(Math.sin(head.yaw), -Math.cos(head.yaw)) : levelForward(f.x, f.z)
}

/** Nearer than this and a placement has no direction from the head worth keeping, metres. */
export const TOWARD_MIN = 0.05

/**
 * The level direction from the head to a placement — where the player's machine *is*, whichever
 * way their head is turned — or `undefined` when the head's own forward is the better answer.
 *
 * **Every wrist-menu press is made looking at the wrist** (the 00:05 session, 14 Sep 2026). The
 * bench, the full-size test cabinet and "Bring it to you" all stood the machine along the head's
 * forward *at the press*, which is the direction of the left hand, not of the machine: the bench
 * appeared off to the side, Leave build put the cabinet back 51° from where the bench had been,
 * and Bring it to you swung the test cabinet toward the wrist. A bench replaces the machine it
 * shrinks, and a test grows the bench where it stands, so both keep the direction from the head
 * to the placement they replace. Undefined — the head's forward instead — when the placement is
 * behind the player (they turned their back; "in front" is the only sensible bench), under the
 * head (no direction to keep), or already dead ahead (so a placement that was stood straight in
 * front of this head reproduces to the byte, which the tests and the "back on the bench" claim
 * both lean on).
 */
export function towardPlacement(head: HeadPose, placement: Placement): LevelDirection | undefined {
  const dx = placement.x - head.x
  const dz = placement.z - head.z
  if (Math.hypot(dx, dz) < TOWARD_MIN) return undefined
  const dir = levelForward(dx, dz)
  const ahead = facing(head)
  const dot = dir.x * ahead.x + dir.z * ahead.z
  if (dot < 0 || dot >= 1 - 1e-9) return undefined
  return dir
}

/** Within this of square, the bench keeps the square of the machine it replaces, radians (45°). */
export const SQUARE_WITHIN = Math.PI / 4

/**
 * The direction to stand the bench along: **square to the machine it replaces** — the way a player
 * standing at that machine's front faces — whenever the author is in front of it.
 *
 * **The bench stood at an angle** (the owner on build 97, 26 Sep 2026: "in build mode, the pinball
 * machine is on an angle to user instead of in front of user properly"). `towardPlacement` aims the
 * bench along the line from the head to the machine's centre, which is right for direction and
 * wrong for yaw: a head 10 cm off the centre line turns a 0.8 m-away machine by 7°, and the bench —
 * 0.36 m away and hand-sized — shows every degree of it. The log had the playing machine at 180°
 * and both benches at 173°. The player was standing square to the machine; the bench keeps that
 * square, centred in front of the head.
 *
 * `towardPlacement` still decides when the author is not in front of it (beyond `SQUARE_WITHIN`,
 * or behind), and when the head already looks square down it (so a placement stood straight in
 * front of this head reproduces to the byte, as before).
 */
export function benchToward(head: HeadPose, placement: Placement): LevelDirection | undefined {
  const toward = towardPlacement(head, placement)
  const square = levelForward(Math.sin(placement.yaw), -Math.cos(placement.yaw))
  const from = toward ?? facing(head)
  const agree = from.x * square.x + from.z * square.z
  const ahead = (placement.x - head.x) * square.x + (placement.z - head.z) * square.z
  if (ahead <= 0 || agree < Math.cos(SQUARE_WITHIN) || agree >= 1 - 1e-9) return toward
  return square
}

/**
 * Where the bench is stood from, and along what: **on the replaced machine's own centre line**,
 * square to it (the owner on build 98, 26 Sep 2026: "pinball machine is still not straight on").
 *
 * `benchToward` gave the bench the machine's square, but it was still centred on the head *at the
 * press* — and the press is made leaning toward the wrist. The log had the head at x −0.13 for the
 * press and back at −0.03 to +0.12 while working, so the bench stood 15–30 cm off the line the
 * author looks down, and read as turned. The head is moved onto the machine's centre line (its
 * distance along the line kept), so the bench lands where the machine was, in front of where the
 * player stood to play it. Where `benchToward` does not use the square, nothing moves.
 */
export function benchFrom(
  head: HeadPose,
  placement: Placement,
): { readonly head: HeadPose; readonly toward: LevelDirection | undefined } {
  const toward = benchToward(head, placement)
  const square = levelForward(Math.sin(placement.yaw), -Math.cos(placement.yaw))
  if (toward === undefined || toward.x !== square.x || toward.z !== square.z) return { head, toward }
  const along = (head.x - placement.x) * square.x + (head.z - placement.z) * square.z
  return { head: { ...head, x: placement.x + square.x * along, z: placement.z + square.z * along }, toward }
}

/**
 * The bench: the machine at `BENCH_SCALE`, its near edge `BENCH_GAP` from the head, facing the
 * head, hanging so its lockdown bar sits the posture's drop below the eyeline.
 *
 * `eyeHeight` is the author's *believed* eye height — the caller resolves an unbelievable floor
 * the way `seatedPlacement` does — and `head.y` is where the bench hangs from. Both are needed:
 * the drop is measured against a person, and the position against the head that is there.
 */
export function benchPlacementFor(
  head: HeadPose,
  cabinet: BenchCabinet,
  eyeHeight = head.y,
  toward?: LevelDirection,
): Placement {
  const drop = eyeHeight < SEATED_HEIGHT ? SEATED_BAR_BELOW_EYELINE : BENCH_BAR_BELOW_EYE
  const barY = head.y - drop
  const floorY = barY - cabinet.lockdownBarHeight * BENCH_SCALE
  return placementInFrontOf(head.x, head.z, facing(head, toward), BENCH_SCALE, cabinet.length, floorY, BENCH_GAP)
}

/** The Full placement: 1:1, on the floor, at the ordinary stand-off, facing the head. */
export function fullPlacementFor(head: HeadPose, cabinet: BenchCabinet, floorY = 0, toward?: LevelDirection): Placement {
  return placementInFrontOf(head.x, head.z, facing(head, toward), 1, cabinet.length, floorY)
}

/**
 * A placement as a claim about the player: the near edge's level distance from the head, its
 * bearing from where the head faces, and how far the lockdown bar sits below the eyes.
 *
 * The three numbers this repository requires every placement claim to be made in — they survive
 * a renumbered tracking space, and they are what a device report can be checked against. Logged
 * by `XRRuntime` at every Build-mode placement so M13-11 reads them off the console rather than
 * off a world coordinate.
 */
export interface HeadRelative {
  /** Metres from the head to the cabinet's near edge, level. */
  readonly nearEdge: number
  /** Radians the cabinet's centre lies off the head's facing; positive is to the player's left. */
  readonly bearing: number
  /** Metres the lockdown bar sits below the eyes. Negative means above them. */
  readonly barDrop: number
  /**
   * Radians the cabinet's front is turned from facing the head; 0 is square on, positive is
   * turned toward the player's left. The number that says "orientating to the wall" (14 Sep 2026)
   * — every placement this file makes faces the head, and this is how a device log shows it.
   */
  readonly facing: number
}

export function headRelative(placement: Placement, head: HeadPose, cabinet: BenchCabinet): HeadRelative {
  const dx = placement.x - head.x
  const dz = placement.z - head.z
  const f = facing(head)
  const along = dx * f.x + dz * f.z
  // The player's left is up × facing = (fz, 0, −fx): for a head looking down −Z, that is −X.
  const across = dx * f.z - dz * f.x
  // The front points (−sin yaw, cos yaw); the head is at (−dx, −dz) from the machine. Positive
  // when the front is turned toward the player's left, the same hand `bearing` counts positive.
  const frontX = -Math.sin(placement.yaw)
  const frontZ = Math.cos(placement.yaw)
  const facingDot = frontX * -dx + frontZ * -dz
  const facingCross = frontZ * -dx - frontX * -dz
  return {
    nearEdge: Math.hypot(dx, dz) - (cabinet.length / 2) * placement.scale,
    bearing: Math.atan2(across, along),
    barDrop: head.y - (placement.y + cabinet.lockdownBarHeight * placement.scale),
    facing: dx === 0 && dz === 0 ? 0 : Math.atan2(facingCross, facingDot),
  }
}

/** What the room says about growing to full. */
export interface FlipRoom {
  /** The ritual's room-fit verdict, or undefined outside mixed reality (no bounds: it fits). */
  readonly fit: RoomFit | undefined
  /** The author's believed eye height, or undefined when nobody has been observed. */
  readonly eyeHeight: number | undefined
  /** Where the floor is in the reference space. 0 in a `local-floor` space. */
  readonly floorY?: number
}

export type FlipOutcome =
  /** Grown to 1:1 on the floor. */
  | 'full'
  /** Testing in place at bench scale, with the reason in `says`. */
  | 'bench'
  /** Not playable: the verdict's own sentence, and nothing moves. */
  | 'refused'

export interface FlipDecision {
  readonly outcome: FlipOutcome
  readonly verdict: TestPlayVerdict
  /** One sentence for the notice panel. */
  readonly says: string
  /** Where the machine goes for the test. The bench itself when the outcome is `bench`. */
  readonly placement: Placement
}


/**
 * Decide the test: playable at all, and if so, at full or on the bench.
 *
 * Pure, so a test can drive every branch; `TestFlip` below is the same decision with the bench
 * remembered across it.
 */
export function decideFlip(
  definition: unknown,
  bench: Placement,
  head: HeadPose,
  cabinet: BenchCabinet,
  room: FlipRoom,
  strings: BenchStrings = BENCH_EN,
  prepared?: TestPlayVerdict,
): FlipDecision {
  const verdict = prepared ?? testPlayVerdict(definition)
  if (!verdict.playable) return { outcome: 'refused', verdict, says: verdict.says, placement: bench }

  if (room.fit !== undefined && !room.fit.fitsFullScale) {
    return { outcome: 'bench', verdict, says: strings['test.shortRoom'], placement: bench }
  }
  const floorY = room.floorY ?? 0
  // The ritual's own rule, asked of the cabinet that would stand there: bar at the floor plus its
  // full height, against the author's eyes. Nobody observed means nobody to refuse for.
  if (room.eyeHeight !== undefined && !barIsPlayable(floorY + cabinet.lockdownBarHeight, room.eyeHeight)) {
    return { outcome: 'bench', verdict, says: strings['test.highBar'], placement: bench }
  }
  // Two rows of forty-two glyphs is what the notice panel has (`sessionNotice.ts`), so the warning
  // count rides on the line rather than the verdict's own sentence, which does not fit beside it.
  const n = verdict.warnings.length
  return {
    outcome: 'full',
    verdict,
    says: n === 0 ? strings['test.full'] : `${strings['test.full']} ${benchCount(strings, 'test.warningOne', 'test.warningMany', n)}`,
    // The bench grows where it stands, square to it on its own centre line — not toward the wrist
    // the press was made from, and not turned by the head's offset from that line (the owner on
    // build 100, 26 Sep 2026: "in test play mode, pinball machine on angle"; the log had the bench
    // at 182° and the test cabinet at 171°).
    placement: fullOnTheBenchLine(head, bench, cabinet, floorY),
  }
}

/** The Full placement grown from the bench: square to it, on its centre line (`benchFrom`). */
function fullOnTheBenchLine(head: HeadPose, bench: Placement, cabinet: BenchCabinet, floorY: number): Placement {
  const from = benchFrom(head, bench)
  return fullPlacementFor(from.head, cabinet, floorY, from.toward)
}

/** The flip, with the bench remembered across it. */
export class TestFlip {
  private bench: Placement | undefined
  private outcomeNow: FlipOutcome | undefined

  /** Is a test running? */
  get testing(): boolean {
    return this.bench !== undefined
  }

  get outcome(): FlipOutcome | undefined {
    return this.outcomeNow
  }

  /**
   * Begin a test from the bench the author is at.
   *
   * A refused verdict starts nothing and remembers nothing, so `end` afterwards is a no-op and the
   * author is still at their bench.
   */
  begin(definition: unknown, bench: Placement, head: HeadPose, cabinet: BenchCabinet, room: FlipRoom, strings: BenchStrings = BENCH_EN, prepared?: TestPlayVerdict): FlipDecision {
    const decision = decideFlip(definition, bench, head, cabinet, room, strings, prepared)
    if (decision.outcome === 'refused') return decision
    // A copy, so a caller that goes on mutating its own placement object cannot move the bench
    // out from under the author while they play.
    this.bench = { x: bench.x, y: bench.y, z: bench.z, yaw: bench.yaw, scale: bench.scale }
    this.outcomeNow = decision.outcome
    return decision
  }

  /**
   * The bench moved under a running test because the space was renumbered (a recycle). Only the
   * mode's own re-derivation calls this; `end` then returns the new bench rather than a
   * coordinate in a space that no longer exists.
   */
  rebench(bench: Placement): void {
    if (this.bench === undefined) return
    this.bench = { x: bench.x, y: bench.y, z: bench.z, yaw: bench.yaw, scale: bench.scale }
  }

  /**
   * Back to build: the bench, byte for byte as it was left, or nothing if no test was running.
   *
   * Returned as a fresh object each time rather than the kept one, for the reason `begin` copies.
   */
  end(): Placement | undefined {
    const bench = this.bench
    this.bench = undefined
    this.outcomeNow = undefined
    if (bench === undefined) return undefined
    return { x: bench.x, y: bench.y, z: bench.z, yaw: bench.yaw, scale: bench.scale }
  }
}
